// Website / funnel builder — block-based editor with live preview link.
// Left rail: pages list. Right pane: page settings + block editor.
import React, { useEffect, useMemo, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Textarea } from '../ui.jsx';

const BLOCK_LABELS = {
  hero: '🎯 Hero', proof: '⭐ Proof bar', text: '📝 Text', services: '🔧 Services',
  reviews: '💬 Reviews', cta: '📣 CTA', form: '📋 Form', image: '🖼 Image',
  direct_answer: '🤖 AI Direct Answer', faq: '❓ FAQ',
  configurator: '🧮 Configurator', blog_list: '📰 Blog list',
  footer: '🦶 Footer',
};

const INDUSTRIES = [
  { id:'hvac',       label:'HVAC' },
  { id:'plumbing',   label:'Plumbing' },
  { id:'electrical', label:'Electrical' },
  { id:'roofing',    label:'Roofing' },
  { id:'garage',     label:'Garage Doors' },
  { id:'handyman',   label:'Handyman' },
];

export function WebsiteBuilder({ user, toast, siteId }) {
  // siteId prop = edit a specific (rank-and-rent) site by id. Otherwise use
  // the current account's default site.
  const [site, setSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [showCityModal, setShowCityModal] = useState(false);
  const [showSiteSettings, setShowSiteSettings] = useState(false);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // API base path differs when editing a rank-and-rent site by id
  const api1 = siteId ? `/api/sites/by-id/${siteId}` : '/api/sites';
  const api2 = siteId ? `/api/sites/by-id/${siteId}/pages` : '/api/sites/pages';

  const loadAll = async () => {
    try {
      const [s, p] = await Promise.all([api(api1), api(api2)]);
      setSite(s); setPages(p || []);
      if (!activeId && p && p.length) setActiveId((p.find(x=>x.isHome) || p[0]).id);
    } catch (e) { setLoadError(e.message); }
  };
  useEffect(() => { loadAll(); }, [siteId]);

  if (loadError) return <div style={{ padding:40 }}>
    <div style={{ ...styles.card, padding:40, textAlign:'center' }}>
      <div style={{ fontSize:48 }}>⚠️</div>
      <h2 style={{ margin:'14px 0 6px' }}>Could not load site</h2>
      <div style={{ color:G.textMuted, fontSize:13 }}>{loadError}</div>
    </div>
  </div>;
  if (!site) return <div style={{ padding:40 }}><Spinner sz={32}/></div>;
  if (site.noAccountContext) return <div style={{ padding:40 }}>
    <div style={{ ...styles.card, padding:40, textAlign:'center' }}>
      <div style={{ fontSize:48 }}>👻</div>
      <h2 style={{ margin:'14px 0 6px' }}>Pick an account first</h2>
      <div style={{ color:G.textMuted, fontSize:14, lineHeight:1.6 }}>{site.note}</div>
    </div>
  </div>;

  const active = pages.find(p => p.id === activeId);

  const newPage = async () => {
    const title = prompt('Page title (e.g. "About", "Services")');
    if (!title?.trim()) return;
    try { const np = await api(api2, { method:'POST', body:{ title, blocks: [] }}); await loadAll(); setActiveId(np.id); toast('Page created'); }
    catch (e) { toast(e.message, 'error'); }
  };

  const delPage = async () => {
    if (!confirm(`Delete "${active.title}"?`)) return;
    const url = siteId ? `/api/sites/by-id/${siteId}/pages/${active.id}` : `/api/sites/pages/${active.id}`;
    try { await api(url, { method:'DELETE' }); await loadAll(); setActiveId(pages.find(p=>p.id!==active.id)?.id); toast('Deleted'); }
    catch (e) { toast(e.message, 'error'); }
  };

  const dupPage = async () => {
    const url = siteId ? `/api/sites/by-id/${siteId}/pages/${active.id}/duplicate` : `/api/sites/pages/${active.id}/duplicate`;
    try { const np = await api(url, { method:'POST' }); await loadAll(); setActiveId(np.id); toast('Duplicated'); }
    catch (e) { toast(e.message, 'error'); }
  };

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:10 }}>
      <div>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>🌐 Website Builder</h1>
        <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>
          {site.publicUrl ? <a href={site.publicUrl} target="_blank" rel="noreferrer" style={{ color:G.cyan }}>{site.publicUrl}</a> : 'Configure your site'}
          {site.published ? <Badge c={G.green} bg={G.greenLight} style={{ marginLeft:8 }}>Live</Badge>
                          : <Badge c={G.yellow} bg={G.yellow+'22'} style={{ marginLeft:8 }}>Draft</Badge>}
        </div>
      </div>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        <Btn sz="sm" onClick={()=>setShowBlogModal(true)}>📝 Auto-Blog</Btn>
        <Btn sz="sm" onClick={()=>setShowCityModal(true)}>🏙 Local SEO Pages</Btn>
        <Btn sz="sm" onClick={()=>setShowSiteSettings(true)}>⚙️ Site Settings</Btn>
        {site.publicUrl && <Btn sz="sm" onClick={()=>window.open(site.publicUrl, '_blank')}>👁 Preview</Btn>}
        <Btn sz="sm" v="primary" onClick={async () => {
          try { await api(api1, { method:'PATCH', body:{ published: !site.published }}); loadAll(); toast(site.published?'Site unpublished':'🚀 Site live'); }
          catch (e) { toast(e.message, 'error'); }
        }}>{site.published ? 'Unpublish' : '🚀 Publish'}</Btn>
      </div>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'260px 1fr', gap:14 }}>
      {/* Pages rail */}
      <div style={{ ...styles.card, padding:8, alignSelf:'flex-start', position:'sticky', top:14 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 8px' }}>
          <strong style={{ fontSize:12, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Pages</strong>
          <Btn sz="xs" v="primary" onClick={newPage}>+</Btn>
        </div>
        {pages.map(p => (
          <div key={p.id} onClick={()=>setActiveId(p.id)} style={{
            padding:'10px 12px', borderRadius:8, cursor:'pointer',
            background: p.id === activeId ? G.purpleDim : 'transparent',
            borderLeft: `3px solid ${p.id === activeId ? G.purple : 'transparent'}`,
            marginBottom:2,
          }}>
            <div style={{ fontWeight:600, fontSize:13 }}>{p.title} {p.isHome && <Badge c={G.cyan} bg={G.cyanLight}>home</Badge>}</div>
            <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>/{p.slug} · {p.blocks.length} blocks</div>
          </div>
        ))}
      </div>

      {/* Editor */}
      {active ? <PageEditor key={active.id} page={active} site={site} siteId={siteId} onChanged={loadAll} onDelete={delPage} onDuplicate={dupPage} toast={toast}/>
              : <div style={{ ...styles.card, padding:60, textAlign:'center', color:G.textMuted }}>Pick a page to edit</div>}
    </div>

    {showSiteSettings && <SiteSettingsModal site={site} siteId={siteId} onClose={()=>setShowSiteSettings(false)} onSaved={()=>{ setShowSiteSettings(false); loadAll(); }} toast={toast}/>}
    {showCityModal && <CityPagesModal onClose={()=>setShowCityModal(false)} onDone={()=>{ setShowCityModal(false); loadAll(); }} toast={toast}/>}
    {showBlogModal && <BlogModal siteId={siteId} onClose={()=>setShowBlogModal(false)} toast={toast}/>}
  </div>;
}

function BlogModal({ siteId, onClose, toast }) {
  const [cfg, setCfg] = useState(null);
  const [topicsText, setTopicsText] = useState('');
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(false);
  const cfgUrl = siteId ? `/api/sites/by-id/${siteId}/blog-config` : '/api/sites/blog-config';
  const listUrl = siteId ? `/api/sites/by-id/${siteId}/blog` : '/api/sites/blog';
  const genUrl = siteId ? `/api/sites/by-id/${siteId}/blog/generate-now` : '/api/sites/blog/generate-now';
  const delUrl = (id) => siteId ? `/api/sites/by-id/${siteId}/blog/${id}` : `/api/sites/blog/${id}`;
  const load = () => Promise.all([api(cfgUrl), api(listUrl)]).then(([c,p]) => {
    setCfg(c); setTopicsText((c.topics||[]).join('\n')); setPosts(p);
  });
  useEffect(() => { load(); }, []);
  if (!cfg) return <Modal open onClose={onClose}><Spinner sz={20}/></Modal>;

  const save = async () => {
    setBusy(true);
    try {
      const topics = topicsText.split('\n').map(s=>s.trim()).filter(Boolean);
      await api(cfgUrl, { method:'PATCH', body:{ enabled: cfg.enabled, cadence: cfg.cadence, topics }});
      toast('✓ Auto-blog saved');
      load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const generateNow = async () => {
    setBusy(true);
    try {
      const r = await api(genUrl, { method:'POST', body: {} });
      toast(`✓ Wrote "${r.post.title}"`);
      load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!confirm('Delete this post?')) return;
    try { await api(delUrl(id), { method:'DELETE' }); load(); }
    catch (e) { toast(e.message, 'error'); }
  };

  return <Modal open onClose={onClose} width={720}>
    <h2 style={{ margin:'0 0 6px', fontSize:18 }}>📝 Auto-Blog</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14, lineHeight:1.6 }}>
      AI writes SEO-tuned blog posts on your cadence. Every post includes FAQ schema, Article schema, and lands in your sitemap automatically. Google indexes them within hours.
    </div>

    <div style={{ ...styles.card, padding:14, marginBottom:14, background:G.input }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
        <strong style={{ fontSize:13 }}>Auto-publish</strong>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
          <input type="checkbox" checked={!!cfg.enabled} onChange={e=>setCfg({...cfg, enabled:e.target.checked})}/>
          {cfg.enabled ? <span style={{ color:G.green, fontWeight:700 }}>ON</span> : <span style={{ color:G.textMuted }}>OFF</span>}
        </label>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <Field label="Cadence">
          <Select value={cfg.cadence||'weekly'} onChange={e=>setCfg({...cfg, cadence:e.target.value})}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Every 2 weeks</option>
            <option value="monthly">Monthly</option>
          </Select>
        </Field>
        <Field label="Next run">
          <Input value={cfg.nextRunAt ? new Date(cfg.nextRunAt).toLocaleString() : '—'} readOnly/>
        </Field>
        <Field label="Topics to rotate through (one per line)" hint="Leave empty to use evergreen 'how to choose / signs you need / DIY vs pro' angles." style={{gridColumn:'1/-1'}}>
          <textarea value={topicsText} onChange={e=>setTopicsText(e.target.value)} rows={5}
            style={{ width:'100%', padding:10, background:G.bg, color:G.text, border:`1px solid ${G.cardBorder}`, borderRadius:6, fontSize:13, fontFamily:'inherit' }}
            placeholder={'How to know if your AC compressor is failing\nSeasonal HVAC maintenance for Tampa homeowners\nReal cost of an AC replacement in 2026'}/>
        </Field>
      </div>
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:10 }}>
        <Btn onClick={generateNow} disabled={busy}>{busy?<Spinner sz={14}/>:'⚡ Generate one now'}</Btn>
        <Btn v="primary" onClick={save} disabled={busy}>Save</Btn>
      </div>
    </div>

    <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>Posts ({posts.length})</div>
    {posts.length === 0 ? <div style={{ color:G.textMuted, fontSize:13, padding:20, textAlign:'center' }}>No posts yet.</div>
      : <div style={{ ...styles.card, padding:0, overflow:'hidden', maxHeight:300, overflowY:'auto' }}>
        {posts.map(p => (
          <div key={p.id} style={{ padding:'10px 14px', borderBottom:`1px solid ${G.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.title} {p.aiGenerated && <Badge c={G.purple} bg={G.purpleDim}>AI</Badge>}</div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{new Date(p.publishedAt||p.createdAt).toLocaleDateString()} · /blog/{p.slug}</div>
            </div>
            <Btn sz="xs" v="danger" onClick={()=>del(p.id)}>×</Btn>
          </div>
        ))}
      </div>}
  </Modal>;
}

function PageEditor({ page, site, siteId, onChanged, onDelete, onDuplicate, toast }) {
  const [blocks, setBlocks] = useState(page.blocks);
  const [title, setTitle] = useState(page.title);
  const [slug, setSlug] = useState(page.slug);
  const [seo, setSeo] = useState(page.seo || {});
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => { setBlocks(page.blocks); setTitle(page.title); setSlug(page.slug); setSeo(page.seo||{}); setDirty(false); }, [page.id]);

  const save = async () => {
    const url = siteId ? `/api/sites/by-id/${siteId}/pages/${page.id}` : `/api/sites/pages/${page.id}`;
    try { await api(url, { method:'PATCH', body:{ title, slug, blocks, seo }}); setDirty(false); onChanged(); toast('✓ Saved'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const markDirty = () => setDirty(true);
  const updateBlock = (id, props) => { setBlocks(blocks.map(b => b.id === id ? { ...b, props: { ...b.props, ...props } } : b)); markDirty(); };
  const removeBlock = (id) => { if (!confirm('Remove this block?')) return; setBlocks(blocks.filter(b => b.id !== id)); markDirty(); };
  const moveBlock = (idx, dir) => {
    const next = [...blocks]; const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]]; setBlocks(next); markDirty();
  };
  // Drag-to-reorder. fromIdx is the source position; toIdx is where it lands.
  // Mirrors moveBlock but supports arbitrary distance.
  const reorderBlocks = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const next = [...blocks];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setBlocks(next); markDirty();
  };
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const addBlock = (type) => {
    setBlocks([...blocks, { id: `${type}-${Math.random().toString(36).slice(2,8)}`, type, props: defaultPropsFor(type) }]);
    setAdding(false); markDirty();
  };

  return <div>
    <div style={{ ...styles.card, padding:14, marginBottom:12 }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto auto auto', gap:10, alignItems:'flex-end' }}>
        <Field label="Page title"><Input value={title} onChange={e=>{ setTitle(e.target.value); markDirty(); }}/></Field>
        <Field label="URL slug" hint={`/site/${site.slug}/${slug}`}><Input value={slug} onChange={e=>{ setSlug(e.target.value); markDirty(); }}/></Field>
        <Btn sz="sm" onClick={onDuplicate}>📑 Duplicate</Btn>
        {!page.isHome && <Btn sz="sm" v="danger" onClick={onDelete}>🗑</Btn>}
        <Btn sz="sm" v="primary" onClick={save} disabled={!dirty}>{dirty ? '💾 Save' : 'Saved'}</Btn>
      </div>
      <details style={{ marginTop:12 }}>
        <summary style={{ cursor:'pointer', fontSize:13, color:G.textMuted, fontWeight:600 }}>🔍 SEO</summary>
        <div style={{ marginTop:10, display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <Field label="Browser title"><Input value={seo.title||''} onChange={e=>{ setSeo({...seo, title:e.target.value}); markDirty(); }}/></Field>
          <Field label="Meta description"><Input value={seo.description||''} onChange={e=>{ setSeo({...seo, description:e.target.value}); markDirty(); }}/></Field>
          <Field label="Keywords (optional)" style={{ gridColumn:'1/-1' }}><Input value={seo.keywords||''} onChange={e=>{ setSeo({...seo, keywords:e.target.value}); markDirty(); }}/></Field>
        </div>
      </details>
    </div>

    {blocks.map((b, i) => (
      <div key={b.id}
        draggable
        onDragStart={(e)=>{ setDragFrom(i); e.dataTransfer.effectAllowed='move'; e.dataTransfer.setData('text/plain', String(i)); }}
        onDragOver={(e)=>{ e.preventDefault(); if (dragOver !== i) setDragOver(i); e.dataTransfer.dropEffect='move'; }}
        onDragLeave={()=>{ if (dragOver === i) setDragOver(null); }}
        onDrop={(e)=>{ e.preventDefault(); const from = dragFrom ?? Number(e.dataTransfer.getData('text/plain')); if (!isNaN(from)) reorderBlocks(from, i); setDragFrom(null); setDragOver(null); }}
        onDragEnd={()=>{ setDragFrom(null); setDragOver(null); }}
        style={{
          opacity: dragFrom === i ? 0.4 : 1,
          borderTop: dragOver === i && dragFrom !== null && dragFrom > i ? `3px solid ${G.purple}` : '3px solid transparent',
          borderBottom: dragOver === i && dragFrom !== null && dragFrom < i ? `3px solid ${G.purple}` : '3px solid transparent',
          transition:'opacity .15s',
        }}>
        <BlockEditor block={b} index={i} count={blocks.length} onUpdate={updateBlock} onRemove={removeBlock} onMove={moveBlock}/>
      </div>
    ))}

    <div style={{ ...styles.card, padding:14, textAlign:'center', borderStyle:'dashed', borderColor:G.cardBorder }}>
      {adding ? (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'center' }}>
          {Object.entries(BLOCK_LABELS).map(([type,label]) => (
            <Btn key={type} sz="sm" onClick={()=>addBlock(type)}>{label}</Btn>
          ))}
          <Btn sz="sm" v="ghost" onClick={()=>setAdding(false)}>Cancel</Btn>
        </div>
      ) : (
        <Btn v="primary" onClick={()=>setAdding(true)}>＋ Add a block</Btn>
      )}
    </div>
  </div>;
}

function defaultPropsFor(type) {
  switch (type) {
    case 'hero':    return { headline:'Big headline here', sub:'Short hook line that closes the deal.', ctaText:'Get Free Quote', ctaHref:'#contact', backgroundStyle:'gradient' };
    case 'proof':   return { text:'⭐ 4.9 stars · Same-day service · 100% guaranteed' };
    case 'text':    return { headline:'Section heading', body:'Your paragraph body. Use this for About, mission, how it works.' };
    case 'services':return { headline:'What we do', items: [{title:'Service A', description:'One line.'}, {title:'Service B', description:'One line.'}] };
    case 'reviews': return { headline:'Reviews', reviews:[{author:'Customer name', rating:5, text:'Great work.'}] };
    case 'cta':     return { headline:'Ready to book?', sub:'Same-day slots open.', ctaText:'Schedule Now', ctaHref:'#contact' };
    case 'form':    return { headline:'Get a free quote', submitLabel:'Send', fields:[{name:'name',label:'Name',type:'text',required:true},{name:'phone',label:'Phone',type:'tel',required:true}] };
    case 'image':   return { url:'', alt:'' };
    case 'direct_answer': return {
      headline:'Quick answers (optimized for ChatGPT / Claude / Perplexity)',
      items:[
        { q:'Who does this service in our area?', a:'We do — licensed, insured, same-day available most weeks.' },
        { q:'How much does it cost?', a:'Up-front flat-rate pricing. Most jobs $89–$450; complex work quoted on-site.' },
        { q:'How fast can you come out?', a:'Same-day slots open most weeks. Book online or call.' },
      ],
    };
    case 'faq':     return { headline:'Frequently asked questions', faqs:[
      { q:'How fast can you come out?', a:'Same-day in most cases. Book online or call.' },
      { q:'What\'s your service area?', a:'We cover the whole metro and 30 miles out.' },
    ]};
    case 'configurator': return {
      headline:'Get an instant estimate', sub:'Pick what you need — see the range before you book.',
      basePrice: 100, submitLabel:'Get my real quote',
      options: [
        { label:'What service do you need?', choices: [
          { label:'AC Repair',     priceAdd: 0 },
          { label:'AC Install',    priceAdd: 4500 },
          { label:'Maintenance',   priceAdd: 89 },
        ]},
        { label:'Urgency', choices: [
          { label:'Today',         priceAdd: 150 },
          { label:'This week',     priceAdd: 0 },
          { label:'Next week+',    priceAdd: -50 },
        ]},
      ],
    };
    case 'blog_list': return { headline:'Latest from the blog' };
    case 'footer':  return { company:'Your business', phone:'', email:'' };
    default:        return {};
  }
}

function BlockEditor({ block, index, count, onUpdate, onRemove, onMove }) {
  const p = block.props || {};
  const update = (k, v) => onUpdate(block.id, { [k]: v });
  return <div style={{ ...styles.card, padding:14, marginBottom:10 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <span title="Drag to reorder" style={{ cursor:'grab', color:G.textMuted, fontSize:16, userSelect:'none' }}>⋮⋮</span>
        <strong style={{ fontSize:13 }}>{BLOCK_LABELS[block.type] || block.type}</strong>
      </div>
      <div style={{ display:'flex', gap:4 }}>
        <Btn sz="xs" disabled={index===0} onClick={()=>onMove(index, -1)}>↑</Btn>
        <Btn sz="xs" disabled={index===count-1} onClick={()=>onMove(index, +1)}>↓</Btn>
        <Btn sz="xs" v="danger" onClick={()=>onRemove(block.id)}>×</Btn>
      </div>
    </div>
    {block.type === 'hero' && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
      <Field label="Headline" style={{ gridColumn:'1/-1' }}><Input value={p.headline||''} onChange={e=>update('headline', e.target.value)}/></Field>
      <Field label="Sub-headline" style={{ gridColumn:'1/-1' }}><Textarea value={p.sub||''} onChange={e=>update('sub', e.target.value)} rows={2}/></Field>
      <Field label="Background image (optional)" style={{ gridColumn:'1/-1' }}>
        <ImagePicker value={p.bgImage} onChange={url=>update('bgImage', url)}/>
      </Field>
      <Field label="Primary CTA text"><Input value={p.ctaText||''} onChange={e=>update('ctaText', e.target.value)}/></Field>
      <Field label="Primary CTA link"><Input value={p.ctaHref||''} onChange={e=>update('ctaHref', e.target.value)} placeholder="#contact or /contact"/></Field>
      <Field label="Secondary CTA text"><Input value={p.secondaryCtaText||''} onChange={e=>update('secondaryCtaText', e.target.value)}/></Field>
      <Field label="Secondary CTA link"><Input value={p.secondaryCtaHref||''} onChange={e=>update('secondaryCtaHref', e.target.value)}/></Field>
      <Field label="Background style"><Select value={p.backgroundStyle||'gradient'} onChange={e=>update('backgroundStyle', e.target.value)}><option value="gradient">Gradient</option><option value="solid">Solid</option></Select></Field>
    </div>}
    {block.type === 'proof' && <Field label="Proof line"><Input value={p.text||''} onChange={e=>update('text', e.target.value)}/></Field>}
    {block.type === 'text' && <div>
      <Field label="Heading (optional)"><Input value={p.headline||''} onChange={e=>update('headline', e.target.value)}/></Field>
      <Field label="Body"><Textarea value={p.body||''} onChange={e=>update('body', e.target.value)} rows={6}/></Field>
    </div>}
    {block.type === 'cta' && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
      <Field label="Headline" style={{ gridColumn:'1/-1' }}><Input value={p.headline||''} onChange={e=>update('headline', e.target.value)}/></Field>
      <Field label="Sub-line" style={{ gridColumn:'1/-1' }}><Input value={p.sub||''} onChange={e=>update('sub', e.target.value)}/></Field>
      <Field label="Button text"><Input value={p.ctaText||''} onChange={e=>update('ctaText', e.target.value)}/></Field>
      <Field label="Button link"><Input value={p.ctaHref||''} onChange={e=>update('ctaHref', e.target.value)}/></Field>
    </div>}
    {block.type === 'image' && <div>
      <Field label="Image" hint="Upload from your computer or paste a URL.">
        <ImagePicker value={p.url} onChange={url=>update('url', url)}/>
      </Field>
      <Field label="Alt text (SEO)" hint="Describe what's in the image — helps Google and screen readers.">
        <Input value={p.alt||''} onChange={e=>update('alt', e.target.value)} placeholder="Tech replacing a 3-ton AC condenser"/>
      </Field>
    </div>}
    {block.type === 'services' && <ServicesEditor p={p} update={update}/>}
    {block.type === 'reviews' && <ReviewsEditor p={p} update={update}/>}
    {block.type === 'form' && <FormEditor p={p} update={update}/>}
    {block.type === 'direct_answer' && <DirectAnswerEditor p={p} update={update}/>}
    {block.type === 'faq' && <FaqEditor p={p} update={update}/>}
    {block.type === 'configurator' && <ConfiguratorEditor p={p} update={update}/>}
    {block.type === 'blog_list' && <Field label="Section heading"><Input value={p.headline||''} onChange={e=>update('headline', e.target.value)}/></Field>}
    {block.type === 'footer' && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
      <Field label="Company name"><Input value={p.company||''} onChange={e=>update('company', e.target.value)}/></Field>
      <Field label="Phone"><Input value={p.phone||''} onChange={e=>update('phone', e.target.value)}/></Field>
      <Field label="Email"><Input value={p.email||''} onChange={e=>update('email', e.target.value)}/></Field>
      <Field label="License # (optional)"><Input value={p.license||''} onChange={e=>update('license', e.target.value)}/></Field>
      <Field label="Address" style={{ gridColumn:'1/-1' }}><Input value={p.address||''} onChange={e=>update('address', e.target.value)}/></Field>
    </div>}
  </div>;
}

function ServicesEditor({ p, update }) {
  const items = p.items || [];
  return <div>
    <Field label="Section heading"><Input value={p.headline||''} onChange={e=>update('headline', e.target.value)}/></Field>
    {items.map((s, i) => (
      <div key={i} style={{ display:'flex', gap:8, marginBottom:6 }}>
        <Input value={s.title||''} onChange={e=>update('items', items.map((x,j) => j===i?{...x, title:e.target.value}:x))} placeholder="Service name"/>
        <Input value={s.description||''} onChange={e=>update('items', items.map((x,j) => j===i?{...x, description:e.target.value}:x))} placeholder="One-line description"/>
        <Btn sz="xs" v="danger" onClick={()=>update('items', items.filter((_,j)=>j!==i))}>×</Btn>
      </div>
    ))}
    <Btn sz="sm" onClick={()=>update('items', [...items, { title:'New service', description:'' }])}>+ Add service</Btn>
  </div>;
}

function ReviewsEditor({ p, update }) {
  const reviews = p.reviews || [];
  return <div>
    <Field label="Section heading"><Input value={p.headline||''} onChange={e=>update('headline', e.target.value)}/></Field>
    {reviews.map((r, i) => (
      <div key={i} style={{ ...styles.card, padding:10, marginBottom:8, background:G.input }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 80px auto', gap:8 }}>
          <Input value={r.author||''} onChange={e=>update('reviews', reviews.map((x,j) => j===i?{...x, author:e.target.value}:x))} placeholder="Author"/>
          <Input type="number" min="1" max="5" value={r.rating||5} onChange={e=>update('reviews', reviews.map((x,j) => j===i?{...x, rating:Number(e.target.value)}:x))}/>
          <Btn sz="xs" v="danger" onClick={()=>update('reviews', reviews.filter((_,j)=>j!==i))}>×</Btn>
        </div>
        <Textarea value={r.text||''} onChange={e=>update('reviews', reviews.map((x,j) => j===i?{...x, text:e.target.value}:x))} rows={2} placeholder="Review text" style={{ marginTop:6 }}/>
      </div>
    ))}
    <Btn sz="sm" onClick={()=>update('reviews', [...reviews, { author:'', rating:5, text:'' }])}>+ Add review</Btn>
  </div>;
}

function FormEditor({ p, update }) {
  const fields = p.fields || [];
  return <div>
    <Field label="Form headline"><Input value={p.headline||''} onChange={e=>update('headline', e.target.value)}/></Field>
    <Field label="Sub-line"><Input value={p.sub||''} onChange={e=>update('sub', e.target.value)}/></Field>
    <Field label="Submit button text"><Input value={p.submitLabel||'Send'} onChange={e=>update('submitLabel', e.target.value)}/></Field>
    <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', margin:'10px 0 6px' }}>Fields</div>
    {fields.map((f, i) => (
      <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 110px 80px auto', gap:6, marginBottom:6 }}>
        <Input value={f.name||''} onChange={e=>update('fields', fields.map((x,j) => j===i?{...x, name:e.target.value}:x))} placeholder="Field key"/>
        <Input value={f.label||''} onChange={e=>update('fields', fields.map((x,j) => j===i?{...x, label:e.target.value}:x))} placeholder="Label"/>
        <Select value={f.type||'text'} onChange={e=>update('fields', fields.map((x,j) => j===i?{...x, type:e.target.value}:x))}>
          {['text','tel','email','select','textarea'].map(t => <option key={t}>{t}</option>)}
        </Select>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}><input type="checkbox" checked={!!f.required} onChange={e=>update('fields', fields.map((x,j) => j===i?{...x, required:e.target.checked}:x))}/>Required</label>
        <Btn sz="xs" v="danger" onClick={()=>update('fields', fields.filter((_,j)=>j!==i))}>×</Btn>
      </div>
    ))}
    <Btn sz="sm" onClick={()=>update('fields', [...fields, { name:'new_field', label:'New field', type:'text' }])}>+ Add field</Btn>
  </div>;
}

function DirectAnswerEditor({ p, update }) {
  const items = p.items || [];
  return <div>
    <Field label="Section heading" hint="Optional — keep crisp. AI crawlers (ChatGPT/Claude/Perplexity) prefer concise Q→A.">
      <Input value={p.headline||''} onChange={e=>update('headline', e.target.value)}/>
    </Field>
    {items.map((f, i) => (
      <div key={i} style={{ ...styles.card, padding:10, marginBottom:8, background:G.input, borderLeft:`3px solid ${G.cyan}` }}>
        <Input value={f.q||''} onChange={e=>update('items', items.map((x,j) => j===i?{...x, q:e.target.value}:x))} placeholder="Question (sounds like a real Google query)" style={{ marginBottom:6 }}/>
        <Textarea value={f.a||''} onChange={e=>update('items', items.map((x,j) => j===i?{...x, a:e.target.value}:x))} rows={2} placeholder="Answer — 1-2 sentences. Specific numbers and named places help AI cite you."/>
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:6 }}>
          <Btn sz="xs" v="danger" onClick={()=>update('items', items.filter((_,j)=>j!==i))}>× Remove</Btn>
        </div>
      </div>
    ))}
    <Btn sz="sm" onClick={()=>update('items', [...items, { q:'New question?', a:'Specific 1-2 sentence answer.' }])}>+ Add Q&A</Btn>
    <div style={{ fontSize:11, color:G.textMuted, marginTop:8, lineHeight:1.5 }}>
      🤖 Each Q&A emits QAPage schema + visible Q→A markup at the top of the page. ChatGPT, Claude, and Perplexity preferentially cite content in this exact format when users ask "best {service} in {city}" questions.
    </div>
  </div>;
}

function FaqEditor({ p, update }) {
  const faqs = p.faqs || [];
  return <div>
    <Field label="Section heading"><Input value={p.headline||''} onChange={e=>update('headline', e.target.value)}/></Field>
    {faqs.map((f, i) => (
      <div key={i} style={{ ...styles.card, padding:10, marginBottom:8, background:G.input }}>
        <Input value={f.q||''} onChange={e=>update('faqs', faqs.map((x,j) => j===i?{...x, q:e.target.value}:x))} placeholder="Question" style={{ marginBottom:6 }}/>
        <Textarea value={f.a||''} onChange={e=>update('faqs', faqs.map((x,j) => j===i?{...x, a:e.target.value}:x))} rows={2} placeholder="Answer"/>
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:6 }}>
          <Btn sz="xs" v="danger" onClick={()=>update('faqs', faqs.filter((_,j)=>j!==i))}>× Remove</Btn>
        </div>
      </div>
    ))}
    <Btn sz="sm" onClick={()=>update('faqs', [...faqs, { q:'New question?', a:'New answer.' }])}>+ Add FAQ</Btn>
    <div style={{ fontSize:11, color:G.textMuted, marginTop:8 }}>FAQ schema markup is added automatically — Google can show these as rich snippets.</div>
  </div>;
}

function ConfiguratorEditor({ p, update }) {
  const options = p.options || [];
  return <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
    <Field label="Headline" style={{gridColumn:'1/-1'}}><Input value={p.headline||''} onChange={e=>update('headline', e.target.value)}/></Field>
    <Field label="Sub-line" style={{gridColumn:'1/-1'}}><Input value={p.sub||''} onChange={e=>update('sub', e.target.value)}/></Field>
    <Field label="Base price ($)"><Input type="number" value={p.basePrice||0} onChange={e=>update('basePrice', Number(e.target.value))}/></Field>
    <Field label="Submit button text"><Input value={p.submitLabel||'Get my quote'} onChange={e=>update('submitLabel', e.target.value)}/></Field>
    <div style={{ gridColumn:'1/-1' }}>
      <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', margin:'8px 0 6px' }}>Question groups</div>
      {options.map((o, i) => (
        <div key={i} style={{ ...styles.card, padding:12, marginBottom:8, background:G.input }}>
          <div style={{ display:'flex', gap:8, marginBottom:6 }}>
            <Input value={o.label||''} onChange={e=>update('options', options.map((x,j) => j===i?{...x, label:e.target.value}:x))} placeholder="Question label" style={{ flex:1 }}/>
            <Btn sz="xs" v="danger" onClick={()=>update('options', options.filter((_,j)=>j!==i))}>×</Btn>
          </div>
          {(o.choices||[]).map((c, k) => (
            <div key={k} style={{ display:'flex', gap:6, marginBottom:4 }}>
              <Input value={c.label||''} onChange={e=>update('options', options.map((x,j) => j===i?{...x, choices: x.choices.map((y,kk) => kk===k?{...y, label:e.target.value}:y)}:x))} placeholder="Choice label"/>
              <Input type="number" value={c.priceAdd||0} onChange={e=>update('options', options.map((x,j) => j===i?{...x, choices: x.choices.map((y,kk) => kk===k?{...y, priceAdd:Number(e.target.value)}:y)}:x))} style={{ width:90 }} placeholder="$ add"/>
              <Btn sz="xs" v="danger" onClick={()=>update('options', options.map((x,j) => j===i?{...x, choices: x.choices.filter((_,kk)=>kk!==k)}:x))}>×</Btn>
            </div>
          ))}
          <Btn sz="xs" onClick={()=>update('options', options.map((x,j) => j===i?{...x, choices: [...(x.choices||[]), { label:'Option', priceAdd:0 }]}:x))}>+ Add choice</Btn>
        </div>
      ))}
      <Btn sz="sm" onClick={()=>update('options', [...options, { label:'New question', choices: [{ label:'Option A', priceAdd:0 }] }])}>+ Add question group</Btn>
    </div>
  </div>;
}

function SiteSettingsModal({ site, siteId, onClose, onSaved, toast }) {
  const [f, setF] = useState(site);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const url = siteId ? `/api/sites/by-id/${siteId}` : '/api/sites';
    try { await api(url, { method:'PATCH', body: f }); onSaved(); toast('Saved'); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const installTpl = async (industry) => {
    if (!confirm(`Install starter pages for ${industry}? This adds new pages — won't delete existing ones unless you confirm.`)) return;
    const replace = confirm('Replace ALL existing pages with starter pages? (Cancel to add alongside.)');
    try { await api(`/api/sites/install-template/${industry}`, { method:'POST', body:{ replaceExisting: replace }}); onSaved(); toast('✓ Template installed'); }
    catch (e) { toast(e.message, 'error'); }
  };
  return <Modal open onClose={onClose} width={620}>
    <h2 style={{ margin:'0 0 14px', fontSize:18 }}>⚙️ Site Settings</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Site name" style={{gridColumn:'1/-1'}}><Input value={f.name||''} onChange={e=>setF({...f, name:e.target.value})}/></Field>
      <Field label="Industry">
        <Select value={f.industry||'hvac'} onChange={e=>setF({...f, industry:e.target.value})}>
          {INDUSTRIES.map(i => <option key={i.id} value={i.id}>{i.label}</option>)}
        </Select>
      </Field>
      <Field label="Location"><Input value={f.location||''} onChange={e=>setF({...f, location:e.target.value})}/></Field>
      <Field label="Phone"><Input value={f.phone||''} onChange={e=>setF({...f, phone:e.target.value})}/></Field>
      <Field label="Business email"><Input value={f.businessEmail||''} onChange={e=>setF({...f, businessEmail:e.target.value})}/></Field>
      <Field label="Logo URL"><Input value={f.logoUrl||''} onChange={e=>setF({...f, logoUrl:e.target.value})}/></Field>
      <Field label="Favicon URL"><Input value={f.faviconUrl||''} onChange={e=>setF({...f, faviconUrl:e.target.value})}/></Field>
      <Field label="Brand color (primary)"><Input type="color" value={f.brandPrimary||'#a78bfa'} onChange={e=>setF({...f, brandPrimary:e.target.value})}/></Field>
      <Field label="Brand color (secondary)"><Input type="color" value={f.brandSecondary||'#22d3ee'} onChange={e=>setF({...f, brandSecondary:e.target.value})}/></Field>
      <Field label="Custom domain (optional)" style={{gridColumn:'1/-1'}} hint="Point your domain's DNS A record to our servers. Setup help coming soon.">
        <Input value={f.customDomain||''} onChange={e=>setF({...f, customDomain:e.target.value})} placeholder="www.example.com"/>
      </Field>
    </div>

    <div style={{ marginTop:18 }}>
      <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>Install starter template</div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
        {INDUSTRIES.map(i => <Btn key={i.id} sz="sm" onClick={()=>installTpl(i.id)}>{i.label}</Btn>)}
      </div>
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:18 }}>
      <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
      <Btn v="primary" onClick={save} disabled={busy}>{busy ? <Spinner sz={14}/> : 'Save'}</Btn>
    </div>
  </Modal>;
}

function CityPagesModal({ onClose, onDone, toast }) {
  const [service, setService] = useState('AC Repair');
  const [citiesText, setCitiesText] = useState('Tampa\nBrandon\nRiverview\nValrico\nPlant City');
  const [busy, setBusy] = useState(false);
  const cities = useMemo(() => citiesText.split('\n').map(s => s.trim()).filter(Boolean), [citiesText]);
  const generate = async () => {
    if (!service.trim() || !cities.length) return toast('Service + at least one city required', 'error');
    setBusy(true);
    try {
      const r = await api('/api/sites/generate-city-pages', { method:'POST', body:{ service, cities }});
      toast(`✓ Generated ${r.created} pages · ${r.skipped} skipped (already exist)`);
      onDone();
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 6px', fontSize:18 }}>🏙 Local SEO Pages</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      Auto-generate one optimized landing page per city for a service. Each page includes the city name in H1, structured data, and a customer form. Google ranks these for "{`{service} in {city}`}" searches.
    </div>
    <Field label="Service to feature">
      <Input value={service} onChange={e=>setService(e.target.value)} placeholder="AC Repair, Roof Inspection, Drain Cleaning…"/>
    </Field>
    <Field label="Cities (one per line)" hint={`${cities.length} cities`}>
      <Textarea value={citiesText} onChange={e=>setCitiesText(e.target.value)} rows={6}/>
    </Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
      <Btn v="primary" onClick={generate} disabled={busy || !cities.length}>{busy ? <Spinner sz={14}/> : `Generate ${cities.length} pages`}</Btn>
    </div>
  </Modal>;
}

// Click-to-upload + URL paste + preview. Uses the existing /api/uploads
// endpoint (base64 in, public URL out) so images are first-class in the
// website builder without forcing operators to host them elsewhere.
function ImagePicker({ value, onChange }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const inputRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { setError('Max 8 MB'); return; }
    setBusy(true); setError(null);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await fetch('/api/uploads', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        credentials:'include',
        body: JSON.stringify({ name: file.name, mime: file.type, base64 }),
      });
      if (!r.ok) {
        const err = await r.json().catch(()=>({})); throw new Error(err.error || `Upload failed (${r.status})`);
      }
      const u = await r.json();
      onChange(u.url);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return <div>
    {value ? <div style={{ ...styles.card, padding:8, position:'relative', display:'flex', gap:10, alignItems:'flex-start' }}>
      <img src={value} alt="" style={{ width:120, height:90, objectFit:'cover', borderRadius:6, background:G.input, flexShrink:0 }}/>
      <div style={{ flex:1, minWidth:0 }}>
        <Input value={value} onChange={e=>onChange(e.target.value)} placeholder="Image URL"/>
        <div style={{ display:'flex', gap:6, marginTop:6 }}>
          <Btn sz="xs" onClick={()=>inputRef.current?.click()} disabled={busy}>{busy?<Spinner sz={12}/>:'⤴ Replace'}</Btn>
          <Btn sz="xs" v="danger" onClick={()=>onChange('')}>×</Btn>
        </div>
      </div>
    </div>
    : <div onClick={()=>inputRef.current?.click()} style={{ ...styles.card, padding:18, textAlign:'center', cursor:'pointer', border:`2px dashed ${G.cardBorder}`, background:G.input+'66' }}>
      <div style={{ fontSize:28 }}>{busy ? <Spinner sz={20}/> : '🖼'}</div>
      <div style={{ fontSize:13, fontWeight:600, marginTop:6 }}>{busy ? 'Uploading…' : 'Click to upload image'}</div>
      <div style={{ fontSize:11, color:G.textMuted, marginTop:4 }}>JPG, PNG, WebP, GIF · up to 8 MB</div>
      <div style={{ fontSize:11, color:G.textMuted, marginTop:8 }}>— or —</div>
      <Input value="" onChange={e=>{ if (e.target.value) onChange(e.target.value); }} placeholder="Paste an image URL" onClick={e=>e.stopPropagation()} style={{ marginTop:6, maxWidth:300, marginInline:'auto' }}/>
    </div>}
    <input ref={inputRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e=>upload(e.target.files?.[0])}/>
    {error && <div style={{ fontSize:12, color:G.red, marginTop:6 }}>{error}</div>}
  </div>;
}
